import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Mail, Key } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/send-otp`, { email });
      toast.success(t('auth.otpSent'));
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, code: otp });
      const { token, user } = response.data;
      
      toast.success(t('auth.loginSuccess'));
      onLogin(token, user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <Building2 className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900">{t('auth.welcomeBack')}</h2>
          <p className="text-gray-600 mt-2">
            {step === 'email' ? t('auth.enterEmail') : t('auth.enterCode')}
          </p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendOTP} className="space-y-6">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {t('email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder={t('auth.emailPlaceholder')}
                data-testid="login-email-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-corporate"
              disabled={loading}
              data-testid="send-otp-button"
            >
              {loading ? t('auth.sending') : t('auth.sendCode')}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-blue-600 hover:underline text-sm"
                data-testid="back-to-home-link"
              >
                {t('auth.backToHome')}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <Label htmlFor="otp" className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                {t('auth.oneTimePassword')}
              </Label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                className="mt-1 text-2xl text-center tracking-widest"
                placeholder="000000"
                maxLength={6}
                data-testid="otp-input"
              />
              <p className="text-xs text-gray-500 mt-2">{t('auth.codeSent')} {email}</p>
            </div>

            <Button
              type="submit"
              className="w-full btn-corporate"
              disabled={loading}
              data-testid="verify-otp-button"
            >
              {loading ? t('auth.verifying') : t('auth.verifyLogin')}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep('email')}
                className="text-blue-600 hover:underline text-sm"
                data-testid="change-email-link"
              >
                {t('auth.changeEmail')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
